import { useCallback, useEffect, useState } from 'react';

import { uploadFileToS3 } from '../app-logs/app-logs.utils';
import { electronApis } from '../electron-api';
import { FEATURES, hasFeature } from '../feature-flags';

import { type FeedbackType } from './feedback.types';

interface UseSnapshotDumpsOptions {
  feedbackType: FeedbackType;
}

export function useSnapshotDumps({ feedbackType }: UseSnapshotDumpsOptions) {
  const isEnabled = hasFeature(FEATURES.SNAPSHOT_DEBUG_DUMP);
  const [apps, setApps] = useState<string[]>([]);
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [isPreparing, setIsPreparing] = useState(false);

  useEffect(() => {
    if (!isEnabled || feedbackType !== 'bug') {
      setApps([]);
      setSelectedApps(new Set());
      return;
    }

    electronApis.snapshotDump
      .listApps()
      .then(setApps)
      .catch(() => setApps([]));
  }, [isEnabled, feedbackType]);

  const toggleApp = useCallback((appName: string) => {
    setSelectedApps((prev) => {
      const next = new Set(prev);
      if (next.has(appName)) {
        next.delete(appName);
      } else {
        next.add(appName);
      }
      return next;
    });
  }, []);

  const uploadSelectedDumps = useCallback(async (): Promise<string[]> => {
    if (selectedApps.size === 0) return [];

    setIsPreparing(true);
    try {
      const archives = await electronApis.snapshotDump.getArchives([
        ...selectedApps,
      ]);

      return await Promise.all(
        archives.map(async ({ filePath, fileName }) => {
          try {
            const fileBytes = await electronApis.system.readFile(filePath);
            const file = new File([fileBytes as BlobPart], fileName, {
              type: 'application/zip',
            });
            return uploadFileToS3(file);
          } finally {
            await electronApis.system.deleteFile(filePath).catch((error) => {
              console.error('Failed to delete prepared snapshot dump:', error);
            });
          }
        })
      );
    } catch (err) {
      console.error('Failed to prepare snapshot dumps:', err);
      return [];
    } finally {
      setIsPreparing(false);
    }
  }, [selectedApps]);

  const shouldShow = isEnabled && feedbackType === 'bug' && apps.length > 0;

  return {
    apps,
    selectedApps,
    toggleApp,
    uploadSelectedDumps,
    isPreparing,
    shouldShow,
  };
}
